import { useState, useEffect, useCallback } from 'react'
import { whatsappApi } from '../api/whatsapp'
import { colors, text, radius, sx } from '../design/tokens'
import Boton from '../components/Boton'
import ConexionVentasWhatsApp from '../components/ConexionVentasWhatsApp'
import { useAuth } from '../context/AuthContext'

/** Comunicaciones → Equipos y etiquetas: líneas conectadas, equipos con sus miembros, etiquetas, estado de la integración. */
export default function WhatsAppConfigPage() {
  const { isSuperAdmin } = useAuth()
  const [estado, setEstado] = useState(null)
  const [lineas, setLineas] = useState([])
  const [equipos, setEquipos] = useState([])
  const [etiquetas, setEtiquetas] = useState([])
  const [agentes, setAgentes] = useState([])
  const [error, setError] = useState('')
  const [nuevaLinea, setNuevaLinea] = useState({ nombre: '', phone_number_id: '', numero_visible: '' })
  const [nuevoEquipo, setNuevoEquipo] = useState('')
  const [nuevaEtq, setNuevaEtq] = useState({ nombre: '', color: colors.textSub })

  const cargar = useCallback(async () => {
    try {
      const [e, l, q, t, a] = await Promise.all([whatsappApi.estado(), whatsappApi.lineas(), whatsappApi.equipos(), whatsappApi.etiquetas(), whatsappApi.agentes()])
      setEstado(e.data); setLineas(l.data); setEquipos(q.data); setEtiquetas(t.data); setAgentes(a.data); setError('')
    } catch (err) {
      if (err.response?.status === 503) setError(err.response.data?.message || 'La bandeja está apagada.')
    }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const run = async (fn) => {
    setError('')
    try { await fn(); await cargar() } catch (e) {
      const d = e.response?.data
      setError(d?.errors ? Object.values(d.errors).flat().join(' ') : (d?.message || 'Error'))
    }
  }

  const toggleMiembro = (equipo, usuarioId) => {
    const ids = equipo.usuarios.map((u) => u.id)
    const nuevos = ids.includes(usuarioId) ? ids.filter((i) => i !== usuarioId) : [...ids, usuarioId]
    run(() => whatsappApi.actualizarEquipo(equipo.id, { usuario_ids: nuevos }))
  }

  return (
    <div>
      <h2 style={st.title}>Equipos y etiquetas</h2>
      <p style={st.sub}>Quién atiende qué en WhatsApp, y las líneas conectadas a LGR.</p>
      {error && <div style={st.error}>{error}</div>}

      {estado && (
        <div style={{ ...sx.card, marginBottom: 16 }}>
          <div style={sx.cardTitle}>Estado de la integración</div>
          <div style={st.flags}>
            <Flag ok={estado.inbox} label="Bandeja" v="WHATSAPP_INBOX_ENABLED" />
            <Flag ok={estado.outbound} label="Envíos" v="WHATSAPP_OUTBOUND_ENABLED" />
            <Flag ok={estado.token} label="Token" v="WHATSAPP_TOKEN" />
            <Flag ok={estado.app_secret} label="Firma del webhook" v="WHATSAPP_APP_SECRET" />
            <Flag ok={estado.verify_token} label="Verify token" v="WHATSAPP_VERIFY_TOKEN" />
            {estado.fake && <Flag ok={false} label="Modo simulación" v="WHATSAPP_FAKE" warn />}
          </div>
          <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 8 }}>
            Webhook para el panel de Meta: <span style={sx.mono}>{window.location.origin}/api/publico/webhooks/whatsapp</span> · suscribir <span style={sx.mono}>messages</span>, <span style={sx.mono}>smb_message_echoes</span>, <span style={sx.mono}>history</span>, <span style={sx.mono}>smb_app_state_sync</span> y <span style={sx.mono}>account_update</span>.
          </div>
        </div>
      )}

      {isSuperAdmin && <ConexionVentasWhatsApp />}

      <div style={st.grid}>
        <div style={sx.card}>
          <div style={sx.cardTitle}>Líneas</div>
          <table style={{ ...sx.table, marginTop: 8 }}>
            <thead><tr><th style={sx.th}>Nombre</th><th style={sx.th}>Número</th><th style={sx.th}>Phone number ID</th><th style={sx.th}>Conv.</th><th style={sx.th}>Activa</th></tr></thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.id} style={st.tr}>
                  <td style={sx.td}>{l.nombre}</td>
                  <td style={{ ...sx.td, ...sx.mono }}>{l.numero_visible || '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono }}>{l.phone_number_id}</td>
                  <td style={{ ...sx.td, ...sx.mono }}>{l.conversaciones_count}</td>
                  <td style={sx.td}><input type="checkbox" checked={!!l.activa} onChange={(e) => run(() => whatsappApi.actualizarLinea(l.id, { activa: e.target.checked }))} /></td>
                </tr>
              ))}
              {lineas.length === 0 && <tr><td style={{ ...sx.td, padding: 12, color: colors.textMeta }} colSpan={5}>Ninguna. Aparece sola con el primer mensaje que llegue, o cargala acá.</td></tr>}
            </tbody>
          </table>
          <form style={st.formRow} onSubmit={(e) => { e.preventDefault(); run(() => whatsappApi.crearLinea(nuevaLinea)).then(() => setNuevaLinea({ nombre: '', phone_number_id: '', numero_visible: '' })) }}>
            <input style={st.input} placeholder="Nombre (ej: Prueba)" value={nuevaLinea.nombre} onChange={(e) => setNuevaLinea({ ...nuevaLinea, nombre: e.target.value })} required />
            <input style={st.input} placeholder="Phone number ID de Meta" value={nuevaLinea.phone_number_id} onChange={(e) => setNuevaLinea({ ...nuevaLinea, phone_number_id: e.target.value })} required />
            <input style={st.input} placeholder="Número visible" value={nuevaLinea.numero_visible} onChange={(e) => setNuevaLinea({ ...nuevaLinea, numero_visible: e.target.value })} />
            <Boton variante="secondary" sm type="submit">+ Línea</Boton>
          </form>
        </div>

        <div style={sx.card}>
          <div style={sx.cardTitle}>Etiquetas</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {etiquetas.map((e) => (
              <span key={e.id} style={{ ...sx.badge, background: e.color ? `${e.color}22` : colors.zonaBg, color: e.color || colors.zonaText, opacity: e.activa ? 1 : 0.45, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {e.nombre}
                <Boton sm variante="ghost" onClick={() => run(() => whatsappApi.actualizarEtiqueta(e.id, { activa: !e.activa }))}>{e.activa ? 'Desactivar' : 'Activar'}</Boton>
              </span>
            ))}
            {etiquetas.length === 0 && <span style={{ fontSize: text.sec, color: colors.textMeta }}>Ninguna todavía.</span>}
          </div>
          <form style={st.formRow} onSubmit={(e) => { e.preventDefault(); run(() => whatsappApi.crearEtiqueta(nuevaEtq)).then(() => setNuevaEtq({ nombre: '', color: colors.textSub })) }}>
            <input style={st.input} placeholder="Nueva etiqueta" value={nuevaEtq.nombre} onChange={(e) => setNuevaEtq({ ...nuevaEtq, nombre: e.target.value })} required />
            <input type="color" value={nuevaEtq.color} onChange={(e) => setNuevaEtq({ ...nuevaEtq, color: e.target.value })} style={{ width: 36, height: 30, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: 0, background: 'none' }} />
            <Boton variante="secondary" sm type="submit">+ Etiqueta</Boton>
          </form>
        </div>
      </div>

      <div style={{ ...sx.card, marginTop: 16 }}>
        <div style={sx.cardTitle}>Equipos y miembros</div>
        <div style={st.equipos}>
          {equipos.map((eq) => (
            <div key={eq.id} style={{ ...st.equipo, opacity: eq.activo ? 1 : 0.5 }}>
              <div style={st.equipoHead}>
                <div style={{ minWidth: 0 }}>
                  <b>{eq.nombre}</b>
                  <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>{eq.slug}</div>
                </div>
                <span style={{ marginLeft: 'auto', flexShrink: 0 }}><Boton sm variante="ghost" onClick={() => run(() => whatsappApi.actualizarEquipo(eq.id, { activo: !eq.activo }))}>{eq.activo ? 'Desactivar' : 'Activar'}</Boton></span>
              </div>
              <div style={st.miembros}>
                {agentes.map((a) => {
                  const dentro = eq.usuarios.some((u) => u.id === a.id)
                  return (
                    <label key={a.id} style={{ ...st.miembro, ...(dentro ? st.miembroOn : {}) }}>
                      <input type="checkbox" checked={dentro} onChange={() => toggleMiembro(eq, a.id)} /> {a.nombre}
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        <form style={st.formRow} onSubmit={(e) => { e.preventDefault(); run(() => whatsappApi.crearEquipo({ nombre: nuevoEquipo })).then(() => setNuevoEquipo('')) }}>
          <input style={st.input} placeholder="Nuevo equipo" value={nuevoEquipo} onChange={(e) => setNuevoEquipo(e.target.value)} required />
          <Boton variante="secondary" sm type="submit">+ Equipo</Boton>
        </form>
      </div>
    </div>
  )
}

function Flag({ ok, label, v, warn }) {
  const color = warn ? colors.warn : ok ? colors.ok : colors.textMeta
  const bg = warn ? colors.warnBg : ok ? colors.okBg : colors.zonaBg
  return (
    <span style={{ ...sx.badge, background: bg, color, display: 'inline-flex', gap: 6, alignItems: 'center', textTransform: 'none', letterSpacing: 0 }}>
      {warn ? '⚠' : ok ? '●' : '○'} {label} <span style={{ ...sx.mono, fontWeight: 400, opacity: 0.8 }}>{v}</span>
    </span>
  )
}

const st = {
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 14px', fontSize: text.body, color: colors.textSec },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.md, padding: '6px 12px', fontSize: text.sec, marginBottom: 12 },
  flags: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 },
  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  formRow: { display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' },
  input: { ...sx.input, flex: 1, minWidth: 140, textTransform: 'none' },
  equipos: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 8 },
  equipo: { border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 10 },
  equipoHead: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: text.body },
  miembros: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  miembro: { fontSize: text.sec, border: `1px solid ${colors.border}`, borderRadius: radius.pill, padding: '2px 8px', cursor: 'pointer', color: colors.textSec, display: 'inline-flex', gap: 4, alignItems: 'center' },
  miembroOn: { background: colors.linkBg, borderColor: colors.linkBorder, color: colors.link },
}
